import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core"
import { Amot360Category, getAmot360 } from "api"
import { getSiteIdByLocale } from "api/SiteId"
import NextLink from "common/NextLink"
import MuiCheckbox from "common/formik/MuiCheckbox"
import MuiInput from "common/formik/MuiInput"
import { withFormik } from "formik"
import { useTranslations } from "next-intl"
import { useRouter } from "next/router"
import React, { useCallback, useEffect, useRef, useState } from "react"
import styled from "styled-components"
import colors from "theme/colors"
import { MainRoutes } from "~/routes/mainRoutes"
import { createSchemaContactUs } from "./SchemaSignUp"

const ORIGIN = process.env.ORIGIN

const StyledContainer = styled.div`
  max-width: 600px;
  width: 100%;
  display: flex;
  flex-direction: column;
`
const StyledFormikInputWrapper = styled.div<{ $isDesktop: "true" | "false" }>`
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;

  ${(p) => (p.$isDesktop === "true" ? `gap: 35px;` : `flex-direction: column;`)}

  textarea {
    &:focus-visible {
      border: none;
    }
  }
`

const StyledButton = styled(Button)<{ $isDesktop: "true" | "false" }>`
  border: 1.5px solid ${(p) => p.theme.colors.black};
  font-size: 13px;
  font-weight: normal;
  border-radius: 5px;
  ${(p) => (p.$isDesktop === "true" ? `width: 200px;` : `width:100%;`)}
`

const StyledPrivacyLink = styled(Typography)`
  /* margin-bottom: 20px; */
  text-decoration: underline;
  font-size: 12px;
  line-height: inherit;
`

const StyledSelect = styled(Select)`
  width: 50%;
  margin-bottom: 20px;
  .MuiSelect-select {
    padding-bottom: 10px;
    padding-top: 25px;
  }
  svg {
    top: calc(50% - 4px);
  }
`
const StyledMuiCheckbox = styled(MuiCheckbox)`
  .MuiTouchRipple-child {
    background-color: ${colors.gray};
  }

  input:focus + svg {
    border: 2px solid blue;
  }
`

const ContactForm = () => {
  const [withCompany, setWithCompany] = useState("")
  const [isAmot360, setIsAmot360] = useState("false")
  const [isDesktop, setIsDesktop] = useState<"true" | "false">("true")
  const [pageName, setPageName] = useState("")
  const [categories, setCategories] = useState<Amot360Category[]>([])
  const [selectedFormName, setSelectedFormName] = useState<string>("")
  const [isAmotConstruction, setIsAmotConstruction] = useState("false")

  const t = useTranslations("contactForm")
  const t360 = useTranslations("amot360")

  const { locale } = useRouter()
  const formEl = useRef(null)
  const firstNameRef = useRef(null)
  const lastNameRef = useRef(null)
  const emailRef = useRef(null)
  const phoneRef = useRef(null)

  const doHtmlFormPost = () => {
    formEl.current.submit()
  }
  const validationSchemaTranslate = createSchemaContactUs(locale as "he" | "en")
  const { query } = useRouter()

  React.useEffect(() => {
    if (query?.isAmot360) {
      fetchCategories()
    }
    setIsAmot360((query?.isAmot360 as string) || "")
    setWithCompany((query?.withCompany as string) || "")
    setPageName((query?.pageName as string) || "")
    setIsDesktop((query?.isDesktop as "true" | "false") || "true")
    setSelectedFormName(
      query?.selectedFormName === "undefined" || !query?.selectedFormName
        ? ""
        : (query?.selectedFormName as string)
    )
    setIsAmotConstruction((query?.isAmotConstruction as string) || "")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  const escFunction = useCallback((event) => {
    if (event.key === "Escape") {
      //Do whatever when esc is pressed
      window.parent.postMessage("closePopup", "*")
    }
  }, [])

  useEffect(() => {
    window.addEventListener("keydown", escFunction, false)
    return () => {
      window.removeEventListener("keydown", escFunction, false)
    }
  }, [escFunction])

  const fetchCategories = async () => {
    const siteId = getSiteIdByLocale(locale)

    const { data } = await getAmot360(siteId)
    setCategories(data.categories)
  }

  const MyForm = withFormik({
    enableReinitialize: true,
    handleSubmit: doHtmlFormPost,
    mapPropsToValues: () => ({
      oid: "00D8d000001FN3O",
      retURL: "https://hauser-marketing.com/amot-sweethome/",
      first_name: "",
      last_name: "",
      email: "",
      mobile: "",
      company: "",
      selectedFormName: selectedFormName || "",
      // howCanWeHelp: "",
      "00N8d0000042MfL": "",
      // checkbox:
      "00N8d000008RbDs": query?.pageName?.includes("contact") ? false : true,
      lead_source: "אתר אמות",
      "00N8d0000045E12": pageName,
      "00N8d00000HQgwx": "",
      remarks: "",
    }),
    validationSchema: validationSchemaTranslate,
  })((props) => {
    const {
      values,
      handleSubmit,
      touched,
      errors,
      handleChange,
      setFieldTouched,
    } = props

    const onChange = (
      event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
      name: string
    ) => {
      event.persist()
      handleChange(event)
      setFieldTouched(name, true, false)
    }

    const getFieldRef = (field: string) => {
      if (field === "first_name") return firstNameRef
      if (field === "last_name") return lastNameRef
      if (field === "email") return emailRef
      if (field === "mobile") return phoneRef
      return null
    }

    const focusOnFirstError = () => {
      const errorKeys = Object.keys(props.errors)

      if (errorKeys.length === 0 && props.dirty) return

      const errorFieldRef = getFieldRef(errorKeys[0] || "first_name")

      setTimeout(() => {
        errorFieldRef?.current.childNodes[1].firstChild.blur()
        errorFieldRef?.current.childNodes[1].firstChild.focus()
      }, 300)
    }

    return (
      <>
        <StyledContainer>
          <form
            action="https://webto.salesforce.com/servlet/servlet.WebToLead?encoding=UTF-8"
            method="POST"
            ref={formEl}
            onSubmit={handleSubmit}
            noValidate
          >
            <StyledFormikInputWrapper $isDesktop={isDesktop}>
              <input type="hidden" name="oid" value="00D8d000001FN3O" />

              <input
                type="hidden"
                name="retURL"
                value={`${ORIGIN}/redirect.html`}
                // value="https://hauser-marketing.com/amot-sweethome/"
              />
              <MuiInput
                variant="standard"
                name={"first_name"}
                label={t("firstName")}
                autoComplete="given-name"
                required
                error={touched.first_name && Boolean(errors.first_name)}
                helperText={
                  touched.first_name && errors.first_name
                    ? errors.first_name
                    : " "
                }
                value={values.first_name}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
                inputRef={firstNameRef}
              />
              <MuiInput
                variant="standard"
                name={"last_name"}
                label={t("lastName")}
                required
                autoComplete="family-name"
                error={touched.last_name && Boolean(errors.last_name)}
                helperText={
                  touched.last_name && errors.last_name ? errors.last_name : " "
                }
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
                inputRef={lastNameRef}
              />
            </StyledFormikInputWrapper>
            <StyledFormikInputWrapper $isDesktop={isDesktop}>
              <MuiInput
                variant="standard"
                name={"email"}
                label={t("email")}
                required
                autoComplete="email"
                error={touched.email && Boolean(errors.email)}
                onChange={onChange}
                helperText={touched.email && errors.email ? errors.email : " "}
                InputLabelProps={{ shrink: true }}
                inputRef={emailRef}
              />
              <MuiInput
                variant="standard"
                name={"mobile"}
                label={t("phoneNumber")}
                required
                autoComplete="tel-national"
                error={touched.mobile && Boolean(errors.mobile)}
                helperText={
                  touched.mobile && errors.mobile ? errors.mobile : " "
                }
                inputRef={phoneRef}
                onChange={onChange}
                InputLabelProps={{ shrink: true }}
              />
            </StyledFormikInputWrapper>

            {(withCompany === "true" || isAmot360 === "true") && (
              <StyledFormikInputWrapper $isDesktop={isDesktop}>
                <MuiInput
                  variant="standard"
                  name={"company"}
                  label={t("companyName")}
                  autoComplete="organization"
                  error={touched.company && Boolean(errors.company)}
                  helperText={touched.company ? errors.company : " "}
                  onChange={onChange}
                  InputLabelProps={{ shrink: true }}
                />
              </StyledFormikInputWrapper>
            )}
            {isAmot360 === "true" && (
              <StyledFormikInputWrapper $isDesktop={isDesktop}>
                <MuiInput
                  id="00N8d0000042MfL"
                  variant="standard"
                  name={"00N8d0000042MfL"}
                  label={t360("remarks")}
                  autoComplete="remarks"
                  error={touched.remarks && Boolean(errors.remarks)}
                  helperText={touched.remarks ? errors.remarks : " "}
                  onChange={onChange}
                  multiline
                  InputLabelProps={{ shrink: true }}
                />
              </StyledFormikInputWrapper>
            )}
            <StyledFormikInputWrapper $isDesktop={isDesktop}>
              {isAmot360 != "true" && (
                <MuiInput
                  variant="standard"
                  name={"00N8d0000042MfL"}
                  label={t("howCanWeHelp")}
                  error={
                    touched["00N8d0000042Mf"] &&
                    Boolean(errors["00N8d0000042Mf"])
                  }
                  helperText={
                    touched["00N8d0000042Mf"] && errors["00N8d0000042Mf"]
                      ? errors["00N8d0000042Mf"]
                      : " "
                  }
                  multiline
                  onChange={onChange}
                  InputLabelProps={{ shrink: true }}
                />
              )}

              {isAmot360 === "true" && (
                <FormControl>
                  <InputLabel id="select-amot360-category-label">
                    {t360("chooseCategory")}
                  </InputLabel>

                  <StyledSelect
                    name={"selectedFormName"}
                    labelId="select-amot360-category-label"
                    id="select-amot360-category"
                    label="choose category"
                    onChange={onChange}
                    value={values.selectedFormName}
                  >
                    {categories?.map((category, i) => (
                      <MenuItem key={i} value={category.formItemName.value}>
                        {category.name.value}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                </FormControl>
              )}
            </StyledFormikInputWrapper>

            <StyledMuiCheckbox
              checked={values["00N8d000008RbDs"]}
              label={t("mailingConf")}
              name="00N8d000008RbDs"
              onChange={onChange}
              error={
                touched["00N8d000008RbDs"] && Boolean(errors["00N8d000008RbDs"])
              }
              value="1"
              helperText={
                touched["00N8d000008RbDs"] && errors["00N8d000008RbDs"]
                  ? errors["00N8d000008RbDs"]
                  : " "
              }
            >
              <NextLink href={MainRoutes.general.privacyPolicy} openInNewTab>
                <StyledPrivacyLink variant="body2">
                  {t("privacyPolice")}
                </StyledPrivacyLink>
              </NextLink>
            </StyledMuiCheckbox>
            {/* utm */}
            {isAmot360 != "true" && (
              <>
                <input
                  id="00N8d0000045ECg"
                  type="hidden"
                  name={"00N8d0000045ECg"}
                  value=""
                />
                <input
                  id="00N8d0000045E12"
                  name="00N8d0000045E12"
                  type="hidden"
                  value={pageName}
                />
              </>
            )}
            {isAmot360 === "true" && (
              <>
                <input
                  id="00N8d00000HQgwx"
                  type="hidden"
                  name={"00N8d00000HQgwx"}
                  value="Services"
                />
                {values.selectedFormName && (
                  <input
                    id={values.selectedFormName}
                    type="hidden"
                    name={values.selectedFormName}
                    value={values.selectedFormName ? "1" : "0"}
                  />
                )}
              </>
            )}
            {isAmotConstruction === "true" && (
              <input
                id="00N8d00000VBI03"
                name="00N8d00000VBI03"
                type="hidden"
                value="Amot construction"
              />
            )}

            <input
              id="lead_source"
              name="lead_source"
              type="hidden"
              value="Amot Website"
            />

            <StyledButton
              variant="outlined"
              type="submit"
              $isDesktop={isDesktop}
              id={isAmot360 ? "amot360-submit-button" : "amot-submit-button"}
              onClick={focusOnFirstError}
            >
              {t("sendButton")}
            </StyledButton>
          </form>
        </StyledContainer>
      </>
    )
  })

  return <MyForm />
}

export default ContactForm
